import { changeLogs, RELEASE_DATE } from '@/config/version';
import { useAppDispatch } from '@/state/hooks';
import { markVersionAsSeen } from '@/state/reducers/appVersionSlice';
import Modal, { ModalClose, ModalHeader, ModalProps, ModalTitle } from '../modal/Modal';
import Divider from './Divider';
interface ChangelogItem {
    type: 'new' | 'improved' | 'fixed' | string;
    title: string;
    description: string;
}
interface ChangeLogsModalProps extends ModalProps {
    version: string;
}

const changes: ChangelogItem[] = changeLogs;

export function ChangeLogsModal(props: ChangeLogsModalProps) {
    const { version, handlers } = props;
    const dispatch = useAppDispatch();

    const typeConfig = {
        new: {
            label: 'New',
            color: 'bg-blue-500',
            textColor: 'text-blue-500',
            bgColor: 'bg-blue-50'
        },
        improved: {
            label: 'Improved',
            color: 'bg-purple-500',
            textColor: 'text-purple-500',
            bgColor: 'bg-purple-50'
        },
        fixed: {
            label: 'Fixed',
            color: 'bg-emerald-500',
            textColor: 'text-emerald-500',
            bgColor: 'bg-brand-neutral-100'
        }
    };

    return (
        <Modal {...props} className="h-auto" size="max-w-xl">
            {/* bg-gradient-to-r from-brand-primary-dark to-brand-background  */}
            <ModalHeader className="px-2 py-4 text-brand-text">
                <ModalTitle className="px-3">
                    <div className="flex items-center gap-3">
                        <div className="text-2xl font-bold">Latest Updates</div>
                        <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm font-semibold">
                            v{version}
                        </span>
                    </div>
                    <p className="text-brand-neutral-400 text-sm">{RELEASE_DATE}</p>
                </ModalTitle>
                <ModalClose
                    {...props}
                    className="pr-2"
                    onClick={() => {
                        dispatch(markVersionAsSeen());
                        handlers.close();
                    }}
                />
            </ModalHeader>
            <Divider />
            <div className="text-sm sm:text-base text-brand-text">
                {/* Content */}
                <div className="px-4 py-4 sm:p-6 sm:px-6 overflow-y-auto max-h-[500px]">
                    <div className="space-y-6">
                        {changes.map((change, index) => {
                            const config = typeConfig[change.type];
                            return (
                                <div key={index} className="flex gap-4 group">
                                    {/* Indicator Line */}
                                    <div className="flex flex-col items-center">
                                        <div className={`w-3 h-3 rounded-full ${config.color}`} />
                                        {index < changes.length - 1 && (
                                            <div className="w-0.5 h-full bg-gradient-to-b from-brand-neutral-500 to-transparent mt-2" />
                                        )}
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 pb-2">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className={`${config.textColor} text-xs font-semibold rounded-full`}>
                                                {config.label}
                                            </span>
                                            <h3 className="font-semibold text-brand-neutral-200 text-sm">
                                                {change.title}
                                            </h3>
                                        </div>
                                        <p className="text-brand-neutral-400 text-sm">{change.description}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            <Divider />
            <div className="text-brand-text p-2 sm:p-4 sm:px-6 text-sm flex space-x-2">
                <button
                    type="button"
                    className="w-full rounded-md p-2 px-4 bg-brand-primary hover:bg-brand-primary-dark"
                    onClick={() => {
                        dispatch(markVersionAsSeen());
                        handlers.close();
                    }}>
                    Noted, thanks!
                </button>
            </div>
        </Modal>
    );
}
